"use client";

import { auth, db } from "@/firebase/firebase";
import useToastNotification from "@/hook/useToastNotification";
import { getStoredTrackingParams } from "@/utils/utmTracker";
import { registerUser } from "@/store/feature/user/userApi";
import { useAppDispatch } from "@/store/hooks";
import { allowedDomains } from "@/utils/Constants";
import {
	getGoogleAuthErrorMessage,
	getGoogleRedirectResult,
	shouldUseGoogleRedirectFallback,
	signInWithGooglePopup,
	signInWithGoogleRedirect,
} from "@/utils/googleAuth";
import { deleteUser, signOut, type UserCredential } from "firebase/auth";
import { deleteDoc, doc, getDoc, setDoc } from "firebase/firestore";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { BackArrow } from "../common/Icons";
import PhoneNumberAuth from "./PhoneNumberAuth";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

const CommonAuth = ({
	customArrowClassName,
	setShow,
	compact = false,
}: {
	customArrowClassName?: string;
	setShow?: React.Dispatch<React.SetStateAction<boolean>>;
	compact?: boolean;
}) => {
	const router = useRouter();
	const currentPath = usePathname();
	const dispatch = useAppDispatch();
	const { showToast } = useToastNotification();
	const [error, setError] = useState<string | null>(null);
	const [isPending, setIsPending] = useState(false);
	const [signInWithEmail, setSignInWithEmail] = useState(false);
	const [signInWithOTP, setSignInWithOTP] = useState(false);
	const [mode, setMode] = useState<"login" | "register">("login");

	const isAllowedEmail = (email: string): boolean => {
		const domain = email.split("@")[1]?.toLowerCase();
		return domain ? allowedDomains.includes(domain) : false;
	};

	const finalizeGoogleSignIn = async (result: UserCredential | null) => {
		const user = result?.user;

		if (!user) {
			return;
		}

		const email = user.email ?? "";
		if (!email) {
			await signOut(auth);
			setError(
				"Google account email is unavailable; please try another account.",
			);
			return;
		}

		if (!isAllowedEmail(email)) {
			await signOut(auth);
			setError(
				"Please use an email address from Gmail, Outlook, Microsoft, Hotmail, or ProtonMail.",
			);
			return;
		}

		const userRef = doc(db, "users", user.uid);
		const userSnap = await getDoc(userRef);

		if (!userSnap.exists()) {
			const trackingParams = getStoredTrackingParams();
			const userDetails = {
				firstName: user.displayName?.split(" ")[0] ?? "",
				lastName: user.displayName?.split(" ")[1] ?? null,
				middleName: null,
				email: user.email ?? "",
				isVerified: true,
				phone: user.phoneNumber ?? null,
				phoneNumberVerified: false,
				userId: user.uid,
				...trackingParams,
				source: trackingParams.utm_campaign || trackingParams.source || null,
			};

			await setDoc(userRef, { ...userDetails, role: "student" });

			try {
				await dispatch(registerUser(userDetails)).unwrap();
			} catch (err) {
				try {
					await deleteDoc(doc(db, "users", user.uid));
					await deleteUser(user);
				} catch (cleanupError) {
					console.error("Cleanup error:", cleanupError);
				}
				console.error("Backend registration failed:", err);
				showToast("Registration failed. Please try again.", "error");
				return;
			}
		}

		localStorage.setItem("userDetails", JSON.stringify(user.uid));
		document.body.classList.remove("!overflow-hidden");
		setShow?.(false);

		if (currentPath === "/auth") {
			router.push("/");
		}
	};

	useEffect(() => {
		let isMounted = true;

		const processRedirectResult = async () => {
			try {
				const result = await getGoogleRedirectResult(auth);
				if (!isMounted || !result) return;

				setIsPending(true);
				setError(null);
				await finalizeGoogleSignIn(result);
			} catch (err) {
				if (!isMounted) return;

				const message = getGoogleAuthErrorMessage(err);
				setError(message);
				console.error("Google redirect sign-in error:", err);
			} finally {
				if (isMounted) {
					setIsPending(false);
				}
			}
		};

		processRedirectResult();

		return () => {
			isMounted = false;
		};
	}, [currentPath, router, setShow]);

	const doSignInWithGoogle = async () => {
		if (isPending) return;
		setIsPending(true);
		setError(null);

		try {
			const result = await signInWithGooglePopup(auth);
			await finalizeGoogleSignIn(result);
		} catch (err) {
			if (shouldUseGoogleRedirectFallback(err)) {
				try {
					await signInWithGoogleRedirect(auth);
					return;
				} catch (redirectError) {
					const redirectMessage = getGoogleAuthErrorMessage(redirectError);
					setError(redirectMessage);
					console.error("Google redirect fallback error:", redirectError);
					return;
				}
			}

			const message = getGoogleAuthErrorMessage(err);
			setError(message);
			console.error("Google sign-in error:", err);
		} finally {
			setIsPending(false);
		}
	};

	return (
		<div
			className={`flex flex-col mx-auto relative ${compact ? "max-w-full" : "max-w-[571px]"
				}`}>
			{(signInWithEmail || signInWithOTP) && (
				<span
					className={`absolute top-[-3%] left-0 size-8 cursor-pointer ${customArrowClassName}`}
					onClick={() => {
						setSignInWithEmail(false);
						setSignInWithOTP(false);
					}}>
					<BackArrow />
				</span>
			)}

			<div
				className={`${compact ? "hidden" : "flex flex-col gap-4 mt-4 sm:mt-5"}`}>
				<p className="ff_poppins text-xl md:text-2xl lg:text-3xl xl:text-[34px] 3xl:text-[38px] font-semibold text-center text-[#130b3c]">
					Your Future Starts Here
				</p>
				<p className="text-center font-medium ff_poppins text-sm sm:text-base xl:text-lg">
					Access your personalised dashboard, track applications, and chat with
					experts.
				</p>
			</div>
			<PhoneNumberAuth setShow={setShow} compact={compact} />
			{/* <div
        className={`${compact ? "hidden" : "flex flex-col gap-4 mt-4 sm:mt-5"}`}
      >
        <p className="ff_poppins text-xl md:text-2xl lg:text-3xl xl:text-[34px] 3xl:text-[38px] font-semibold text-center text-[#130b3c]">
          {mode === "login"
            ? "Continue Your Journey"
            : "Get Started with Edvia"}
        </p>
        <p className="text-center font-medium ff_poppins text-sm sm:text-base xl:text-lg">
          Your gateway to personalised course matches, expert support, and
          application tracking
        </p>
      </div>
      {!signInWithEmail && !signInWithOTP && (
        <div>
          <div className={compact ? "pt-2" : "pt-[24px]"}>
            <button
              onClick={() => {
                if (isPending) return;
                doSignInWithGoogle();
                sendGTMEvent({
                  event: "buttonClicked",
                  value: "register-with-google-button",
                });
              }}
              type="button"
              disabled={isPending}
              className={`border border-black flex items-center gap-5 justify-center rounded-[4px] w-full ${
                compact ? "py-2.5 text-base" : "py-3 3xl:py-[16px]"
              } ${isPending ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <span className="size-6 md:size-7 3xl:size-9">
                <Google />
              </span>
              <span className="inline-block text-base ff_poppins sm:text-lg">
                {mode === "login" ? "Login with Google" : "Sign up with Google"}
              </span>
            </button>
            {error && (
              <span className="text-[#CD2C2C] capitalize ff_poppins text-xs sm:text-sm font-medium leading-[16.41px] inline-block">
                {error}
              </span>
            )}
          </div>

      <div className={compact ? "pt-2" : "pt-[24px]"}>
        <button
          onClick={() => {
            if (isPending) return;
            void sendCustomGaEvent("auth_google_click", {
              path: currentPath,
              compact,
              mode: "combined",
            });
            sendGTMEvent({
              event: "buttonClicked",
              value: "register-with-google-button",
            });
            void doSignInWithGoogle();
          }}
          type="button"
          disabled={isPending}
          className={`border border-black flex items-center gap-5 justify-center rounded-[4px] w-full ${
            compact ? "py-2.5 text-base" : "py-3 3xl:py-[16px]"
          } ${isPending ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <span className="size-6 md:size-7 3xl:size-9">
            <Google />
          </span>
          <span className="inline-block text-base ff_poppins sm:text-lg">
            Continue with Google
          </span>
        </button>
        {error && (
          <span className="text-[#CD2C2C] capitalize ff_poppins text-xs sm:text-sm font-medium leading-[16.41px] inline-block mt-2">
            {error}
          </span>
        )}
      </div>

      {compact ? (
        <div className="my-3 flex items-center gap-3 text-gray-300">
          <div className="h-[1px] flex-1 bg-gray-200"></div>
          <span className="text-sm ff_poppins text-[#3777ff]">OR</span>
          <div className="h-[1px] flex-1 bg-gray-200"></div>
        </div>
      )}
      {signInWithEmail && <CustomRegisterLoginWithEmail />}
      {signInWithOTP && <PhoneNumberAuth setShow={setShow} compact={compact} />} */}
		</div>
	);
};

export default CommonAuth;
